import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/utils/constants";
import { useT } from "@/i18n/LanguageContext";
import { Languages } from "lucide-react";

const ServiceIntervalModal = ({ open, onClose, interval, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    interval_value: "",
    interval_unit: "days",
    applies_to: ["tractor", "chassis", "yard_equipment"],
  });
  const [loading, setLoading] = useState(false);

  const vehicleTypeOptions = [
    { id: "tractor", label: "Tractors" },
    { id: "chassis", label: "Chassis" },
    { id: "yard_equipment", label: "Yard Equipment" },
  ];

  const isOnDemand = formData.interval_unit === "on_demand";

  useEffect(() => {
    if (interval) {
      setFormData({
        name: interval.name || "",
        interval_value: interval.interval_value ? interval.interval_value.toLocaleString() : "",
        interval_unit: interval.interval_unit || "days",
        applies_to: interval.applies_to || ["tractor", "chassis", "yard_equipment"],
      });
    } else {
      setFormData({
        name: "",
        interval_value: "",
        interval_unit: "days",
        applies_to: ["tractor", "chassis", "yard_equipment"],
      });
    }
  }, [interval, open]);

  const handleIntervalValueChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value) || 0;
      setFormData({ ...formData, interval_value: numValue > 0 ? numValue.toLocaleString() : '' });
    }
  };

  const handleVehicleTypeToggle = (typeId) => {
    const current = formData.applies_to || [];
    if (current.includes(typeId)) {
      setFormData({ ...formData, applies_to: current.filter(t => t !== typeId) });
    } else {
      setFormData({ ...formData, applies_to: [...current, typeId] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.applies_to.length === 0) {
      toast.error("Please select at least one vehicle type");
      return;
    }
    const intervalValue = parseInt(String(formData.interval_value).replace(/,/g, '')) || 0;
    // Only require interval value if not on-demand
    if (formData.interval_unit !== "on_demand" && intervalValue <= 0) {
      toast.error("Please enter a valid interval value");
      return;
    }
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        interval_value: formData.interval_unit === "on_demand" ? 0 : intervalValue
      };
      if (interval) {
        await axios.put(`${API}/service-intervals/${interval.id}`, submitData);
        toast.success("Interval updated");
      } else {
        await axios.post(`${API}/service-intervals`, submitData);
        toast.success("Interval created");
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to save interval");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] sm:max-w-lg sm:h-auto sm:max-h-[90vh] m-0 sm:m-auto rounded-none sm:rounded-lg flex flex-col fixed inset-0 sm:inset-auto p-0 overflow-hidden">
        <DialogHeader className="flex-shrink-0 p-4 border-b bg-white">
          <DialogTitle>{interval ? "Edit Interval" : "Add Service Interval"}</DialogTitle>
        </DialogHeader>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="interval_name">Name *</Label>
              <Input
                id="interval_name"
                data-testid="input-interval-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., B.I.T. Inspection"
                required
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="interval_unit">Type</Label>
                <Select 
                  value={formData.interval_unit} 
                  onValueChange={(v) => setFormData({ ...formData, interval_unit: v, interval_value: v === "on_demand" ? "" : formData.interval_value })}
                >
                  <SelectTrigger data-testid="select-interval-unit" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_demand">On Demand (No Schedule)</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="months">Months</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="miles">Miles</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="gallons">Gallons</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="interval_value">Interval Value {!isOnDemand && "*"}</Label>
                <Input
                  id="interval_value"
                  type="text"
                  data-testid="input-interval-value"
                  value={formData.interval_value}
                  onChange={handleIntervalValueChange}
                  placeholder={isOnDemand ? "N/A" : "e.g., 90 or 10,000"}
                  disabled={isOnDemand}
                  required={!isOnDemand}
                  className={`mt-1 ${isOnDemand ? 'bg-slate-100 text-slate-400' : ''}`}
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Applies To *</Label>
              <div className="flex flex-wrap gap-2">
                {vehicleTypeOptions.map((type) => (
                  <label 
                    key={type.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      formData.applies_to?.includes(type.id) 
                        ? 'bg-blue-50 border-blue-300 text-blue-700' 
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.applies_to?.includes(type.id) || false}
                      onChange={() => handleVehicleTypeToggle(type.id)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      data-testid={`checkbox-applies-${type.id}`}
                    />
                    <span className="text-sm font-medium">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Footer Buttons */}
        <div className="flex-shrink-0 flex justify-end gap-3 p-4 border-t bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <Button type="button" variant="outline" onClick={onClose} className="h-10 px-4">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="btn-save-interval" className="h-10 px-4">
            {loading ? "Saving..." : interval ? "Update" : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SettingsSection = ({ serviceIntervals, onUpdate, currentUser }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingInterval, setEditingInterval] = useState(null);
  const { t, lang, setLang } = useT();
  
  // Import permissions
  const { hasPermission, PERMISSIONS } = require("@/utils/permissions");
  const userRole = currentUser?.role;
  const canAdd = hasPermission(userRole, PERMISSIONS.SETTINGS.ADD_INTERVAL);
  const canEdit = hasPermission(userRole, PERMISSIONS.SETTINGS.EDIT_INTERVAL);
  const canDelete = hasPermission(userRole, PERMISSIONS.SETTINGS.DELETE_INTERVAL);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pt-16 sm:pt-6 lg:pt-8 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t("Settings")}</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">{t("Manage service intervals and configurations")}</p>
        </div>
        {canAdd && (
          <Button onClick={() => setShowAddModal(true)} data-testid="btn-add-interval" className="shrink-0">
            <Plus className="w-4 h-4 mr-2" /> {t("Add Interval")}
          </Button>
        )}
      </div>

      {/* Language selector */}
      <div className="bg-white rounded-xl border border-slate-200 mb-6" data-testid="language-setting">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Languages className="w-4 h-4 text-blue-500" /> {t("App Language")}</h3>
          <p className="text-sm text-slate-500">{t("Choose the language for the entire app.")}</p>
        </div>
        <div className="p-4 flex flex-wrap gap-2">
          {[{ code: "en", label: t("English") }, { code: "es", label: t("Spanish (Mexico)") }].map((opt) => (
            <button
              key={opt.code}
              onClick={() => setLang(opt.code)}
              data-testid={`lang-${opt.code}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                lang === opt.code ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Service Intervals</h3>
          <p className="text-sm text-slate-500">Configure when services are due</p>
        </div>
        <div className="divide-y divide-slate-200">
          {serviceIntervals.map((interval) => (
            <div 
              key={interval.id} 
              className="p-4 flex items-center justify-between hover:bg-slate-50"
              data-testid={`interval-${interval.id}`}
            >
              <div>
                <div className="font-medium text-slate-900">{interval.name}</div>
                <div className="text-sm text-slate-500">
                  {interval.interval_unit === "on_demand" ? (
                    <span className="text-purple-600 font-medium">On Demand</span>
                  ) : (
                    <>Every {(interval.interval_value || 0).toLocaleString()} {interval.interval_unit}</>
                  )}
                </div>
                <div className="flex gap-1 mt-2">
                  {interval.applies_to?.includes("tractor") && (
                    <span className="type-badge tractor text-[10px]">Tractors</span>
                  )}
                  {interval.applies_to?.includes("chassis") && (
                    <span className="type-badge chassis text-[10px]">Chassis</span>
                  )}
                  {interval.applies_to?.includes("yard_equipment") && (
                    <span className="type-badge yard_equipment text-[10px]">Yard Equipment</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canEdit && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setEditingInterval(interval)}
                    data-testid={`btn-edit-interval-${interval.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                {canDelete && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={async () => {
                      if (window.confirm("Delete this interval?")) {
                        await axios.delete(`${API}/service-intervals/${interval.id}`);
                        toast.success("Interval deleted");
                        onUpdate();
                      }
                    }}
                    data-testid={`btn-delete-interval-${interval.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          {serviceIntervals.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              No service intervals configured
            </div>
          )}
        </div>
      </div>

      <ServiceIntervalModal
        open={showAddModal || !!editingInterval}
        onClose={() => { setShowAddModal(false); setEditingInterval(null); }}
        interval={editingInterval}
        onSave={onUpdate}
      />
    </div>
  );
};

export { ServiceIntervalModal };
